/**
 * Google Calendar two-way client (browser-only).
 *
 * Obtains an OAuth access token by re-running the Google sign-in popup with the
 * Calendar scope, then talks to the Calendar v3 REST API directly. Tokens last
 * ~1 hour and there is no refresh token in the client flow, so we cache the
 * token in sessionStorage and prompt to reconnect when it expires (401).
 *
 * CRM events are matched to Google events by a private extended property
 * `mfxKey`, so pushing the same CRM event twice updates rather than duplicates.
 */
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from './firebase';

const SCOPE = 'https://www.googleapis.com/auth/calendar.events';
const SS_KEY = 'mfx-gcal-token';
const API = 'https://www.googleapis.com/calendar/v3';
const HD = (import.meta as any).env?.VITE_AUTH_HOSTED_DOMAIN || 'microflexfilm.com';

interface Tok { token: string; exp: number; }

function load(): Tok | null {
  try { const r = sessionStorage.getItem(SS_KEY); if (r) { const t = JSON.parse(r) as Tok; if (t.exp > Date.now()) return t; } } catch { /* ignore */ }
  return null;
}
let mem: Tok | null = load();

function store(t: Tok) { mem = t; try { sessionStorage.setItem(SS_KEY, JSON.stringify(t)); } catch { /* ignore */ } }

export function gcalConnected(): boolean { return !!(mem && mem.exp > Date.now()); }
export function gcalDisconnect() { mem = null; try { sessionStorage.removeItem(SS_KEY); } catch { /* ignore */ } }

/** Run the Google consent popup and capture a Calendar access token. */
export async function gcalConnect(): Promise<boolean> {
  const provider = new GoogleAuthProvider();
  provider.addScope(SCOPE);
  provider.setCustomParameters({ prompt: 'consent', hd: HD });
  const result = await signInWithPopup(auth, provider);
  const cred = GoogleAuthProvider.credentialFromResult(result);
  const token = cred?.accessToken;
  if (!token) return false;
  store({ token, exp: Date.now() + 55 * 60 * 1000 });
  return true;
}

async function call(path: string, init?: RequestInit): Promise<any> {
  if (!mem) throw new Error('Not connected to Google Calendar.');
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${mem.token}`, 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  if (res.status === 401) { gcalDisconnect(); throw new Error('Google session expired — reconnect.'); }
  if (!res.ok) { throw new Error(`Google Calendar error ${res.status}`); }
  return res.status === 204 ? null : res.json();
}

export interface GEvent {
  id?: string; summary?: string; description?: string;
  start?: { date?: string; dateTime?: string }; end?: { date?: string; dateTime?: string };
  htmlLink?: string; extendedProperties?: { private?: Record<string, string> };
}

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export async function gcalList(timeMin: Date, timeMax: Date): Promise<GEvent[]> {
  const p = new URLSearchParams({ timeMin: timeMin.toISOString(), timeMax: timeMax.toISOString(), singleEvents: 'true', orderBy: 'startTime', maxResults: '250' });
  const r = await call(`/calendars/primary/events?${p.toString()}`);
  return (r && r.items) || [];
}

export interface PushEvent { key: string; title: string; date: Date; allDay: boolean; time?: string; detail?: string; }

function toGoogleBody(ev: PushEvent) {
  const start = ev.allDay ? { date: ymd(ev.date) } : { dateTime: ev.date.toISOString() };
  const endDate = ev.allDay ? addDays(ev.date, 1) : new Date(ev.date.getTime() + 60 * 60 * 1000);
  const end = ev.allDay ? { date: ymd(endDate) } : { dateTime: endDate.toISOString() };
  return { summary: ev.title, description: ev.detail || '', start, end, extendedProperties: { private: { mfxKey: ev.key, mfxSource: 'mfx-crm' } } };
}

/** Create or update a single CRM event on the primary calendar (idempotent by mfxKey). */
export async function gcalUpsert(ev: PushEvent): Promise<GEvent> {
  const p = new URLSearchParams({ privateExtendedProperty: `mfxKey=${ev.key}`, maxResults: '1' });
  const found = await call(`/calendars/primary/events?${p.toString()}`);
  const body = toGoogleBody(ev);
  if (found && found.items && found.items[0]) {
    return call(`/calendars/primary/events/${found.items[0].id}`, { method: 'PATCH', body: JSON.stringify(body) });
  }
  return call('/calendars/primary/events', { method: 'POST', body: JSON.stringify(body) });
}

/** Push many CRM events sequentially. Returns how many succeeded. */
export async function gcalPushAll(events: PushEvent[], cap = 60): Promise<{ pushed: number; failed: number }> {
  let pushed = 0, failed = 0;
  for (const ev of events.slice(0, cap)) {
    try { await gcalUpsert(ev); pushed++; } catch { failed++; }
  }
  return { pushed, failed };
}
